import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Building2, CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { showSuccess } from '../../lib/toast';
import { Modal } from '../shared/Modal';

/**
 * Modal super-admin : création d'un tenant + son 1er administrateur en une
 * seule passe. Le backend `POST /platform/tenants` :
 *
 *  - valide unicité code + sous-domaine + email admin
 *  - clone les 2 rôles système pour le tenant
 *  - génère un mdp temporaire conforme à la politique par défaut
 *  - persiste user `must_change_password=true`
 *  - seed les templates email par défaut (user_invitation, password_reset…)
 *  - renvoie `{ tenant, admin, temporary_password }` (le mdp est exposé en
 *    dev pour permettre la transmission manuelle ; en prod il faudra
 *    brancher l'envoi email via Resend / Mailhog)
 */

interface FormValues {
  code: string;
  nom: string;
  sous_domaine: string;
  // Contact principal
  contact_nom: string;
  contact_prenom: string;
  contact_email: string;
  contact_telephone: string;
  // Admin
  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;
}

interface CreatedResult {
  tenant: { id: string; code: string; nom: string };
  admin: { email: string; first_name: string; last_name: string };
  temporary_password?: string;
}

export function CreateTenantModal({ onClose, onCreated }: { onClose(): void; onCreated(): void }) {
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedResult | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      code: '',
      nom: '',
      sous_domaine: '',
      contact_nom: '',
      contact_prenom: '',
      contact_email: '',
      contact_telephone: '',
      admin_first_name: '',
      admin_last_name: '',
      admin_email: '',
    },
  });

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    setErrMsg(null);
    try {
      const payload = {
        code: values.code.trim(),
        nom: values.nom.trim(),
        ...(values.sous_domaine.trim()
          ? { sous_domaine: values.sous_domaine.trim().toLowerCase() }
          : {}),
        contact_principal: {
          nom: values.contact_nom.trim(),
          prenom: values.contact_prenom.trim(),
          email: values.contact_email.trim(),
          ...(values.contact_telephone.trim()
            ? { telephone: values.contact_telephone.trim() }
            : {}),
        },
        admin: {
          first_name: values.admin_first_name.trim(),
          last_name: values.admin_last_name.trim(),
          email: values.admin_email.trim(),
        },
      };
      const res = await api.post<{ data: CreatedResult }>('/platform/tenants', payload);
      setResult(res.data.data);
      showSuccess(`Tenant ${payload.code} créé avec succès`);
    } catch (err) {
      setErrMsg(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyPassword() {
    if (!result?.temporary_password) return;
    await navigator.clipboard.writeText(result.temporary_password);
    showSuccess('Mot de passe temporaire copié');
  }

  // ─── État succès : affichage du récap + mdp temporaire ─────────────────
  if (result) {
    return (
      <Modal
        title="Tenant créé"
        titleIcon={<CheckCircle2 className="w-5 h-5 text-green-600" />}
        width="lg"
        onClose={() => {
          onCreated();
          onClose();
        }}
        footer={
          <button
            type="button"
            onClick={() => {
              onCreated();
              onClose();
            }}
            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            Terminé
          </button>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            Le tenant <strong>{result.tenant.nom}</strong> a été créé. Les rôles système
            (Administrateur tenant + Utilisateur standard) et les modèles email par défaut sont déjà
            configurés.
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Code</span>
              <span className="font-mono">{result.tenant.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Nom</span>
              <span className="font-medium text-gray-900">{result.tenant.nom}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Admin</span>
              <span className="font-medium text-gray-900">
                {result.admin.first_name} {result.admin.last_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Email admin</span>
              <span className="font-mono text-xs">{result.admin.email}</span>
            </div>
          </div>

          {result.temporary_password && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-xs text-amber-900 font-medium">
                ⚠️ Mot de passe temporaire — à transmettre à l'administrateur en main propre
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded bg-white border border-amber-300 font-mono text-sm break-all">
                  {result.temporary_password}
                </code>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="p-2 rounded-lg border border-amber-300 hover:bg-amber-100 text-amber-700"
                  title="Copier"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-amber-700">
                L'admin devra changer son mot de passe à la première connexion. Cette valeur
                n'apparaîtra plus.
              </p>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ─── État formulaire ───────────────────────────────────────────────────
  return (
    <Modal
      title="Nouveau tenant"
      titleIcon={<Building2 className="w-5 h-5 text-primary-600" />}
      width="2xl"
      locked={submitting}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="create-tenant-form"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Créer le tenant
          </button>
        </>
      }
    >
      <form id="create-tenant-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {errMsg && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errMsg}</p>
          </div>
        )}

        <Section title="Identité du tenant">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Code *" error={errors.code?.message}>
              <input
                {...register('code', {
                  required: 'Requis',
                  minLength: { value: 2, message: '2 caractères minimum' },
                  maxLength: { value: 50, message: '50 caractères maximum' },
                })}
                placeholder="Identifiant unique du tenant"
                className={inputClass}
              />
            </Field>
            <Field label="Nom *" error={errors.nom?.message}>
              <input
                {...register('nom', { required: 'Requis', minLength: 2 })}
                placeholder="Hôpital Paris 13"
                className={inputClass}
              />
            </Field>
            <Field
              label="Sous-domaine"
              error={errors.sous_domaine?.message}
              className="md:col-span-2"
            >
              <input
                {...register('sous_domaine', {
                  pattern: {
                    value: /^[a-z0-9-]*$/,
                    message: 'kebab-case : lettres minuscules, chiffres, tirets',
                  },
                })}
                placeholder="hopital-paris-13 (optionnel)"
                className={inputClass + ' lowercase'}
              />
            </Field>
          </div>
        </Section>

        <Section title="Contact principal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Prénom *" error={errors.contact_prenom?.message}>
              <input
                {...register('contact_prenom', { required: 'Requis' })}
                className={inputClass}
              />
            </Field>
            <Field label="Nom *" error={errors.contact_nom?.message}>
              <input {...register('contact_nom', { required: 'Requis' })} className={inputClass} />
            </Field>
            <Field label="Email *" error={errors.contact_email?.message}>
              <input
                type="email"
                {...register('contact_email', {
                  required: 'Requis',
                  pattern: { value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: 'Email invalide' },
                })}
                className={inputClass}
              />
            </Field>
            <Field label="Téléphone" error={errors.contact_telephone?.message}>
              <input
                {...register('contact_telephone')}
                placeholder="+33 1 00 00 00 00"
                className={inputClass}
              />
            </Field>
          </div>
        </Section>

        <Section title="Administrateur du tenant">
          <p className="text-xs text-gray-500 mb-3">
            Cet utilisateur recevra le rôle "Administrateur tenant" et un mot de passe temporaire
            (affiché après création). Il devra le changer à la 1<sup>re</sup> connexion.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Prénom *" error={errors.admin_first_name?.message}>
              <input
                {...register('admin_first_name', { required: 'Requis' })}
                className={inputClass}
              />
            </Field>
            <Field label="Nom *" error={errors.admin_last_name?.message}>
              <input
                {...register('admin_last_name', { required: 'Requis' })}
                className={inputClass}
              />
            </Field>
            <Field label="Email *" error={errors.admin_email?.message} className="md:col-span-2">
              <input
                type="email"
                {...register('admin_email', {
                  required: 'Requis',
                  pattern: { value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: 'Email invalide' },
                })}
                placeholder="admin@hopital-paris-13.fr"
                className={inputClass}
              />
            </Field>
          </div>
        </Section>
      </form>
    </Modal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-3">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
